import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import {
  computeCharacterCombatStats,
  createSeededRng,
  startDungeonRun,
  buildEquipmentContextFromLoadout,
  createEquipmentSnapshot,
} from '../../domain';
import { MINI_DUNGEON_NAME } from '../../data/dungeon';
import {
  DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE,
  DUNGEON_MUTATIONS,
  clampPlayableDungeonLevel,
  describeProgressionChallenge,
  type DungeonMutationId,
} from '../../data/dungeonProgression';
import {
  buildSavedGame,
  createSaveStore,
  dungeonFromSave,
} from '../../persistence/saveStore';
import {
  SAVE_SCHEMA_VERSION,
  createDefaultPermanentSave,
  inventoryFromPermanent,
} from '../../persistence/schema';
import {
  markAwaitingSettlement,
  settleRun,
} from '../../persistence/settlement';
import { paintInteriorBackdrop } from '../village/art';
import {
  addBackToVillageButton,
  addPrimaryButton,
  addSecondaryButton,
  titleBlock,
} from '../ui/hubUi';

const MUTATION_LABELS: Record<DungeonMutationId, string> = {
  standard: 'Standard',
  hard: 'Hard',
  brutal: 'Brutal',
};

/**
 * Dungeon Gate — dungeon select, numerical level, mutation, challenge/reward cue.
 * Portrait-first. Unrestricted selection within the initial playable range
 * until progression unlocking is designed.
 */
export class DungeonGateScene extends Phaser.Scene {
  private abandonConfirm?: Phaser.GameObjects.Container;
  private selectedMutation: DungeonMutationId = 'standard';
  private selectedDungeonLevel: number = DUNGEON_LEVEL_INITIAL_PLAYABLE_RANGE.min;
  private progressionHint?: Phaser.GameObjects.Text;
  private levelLabel?: Phaser.GameObjects.Text;

  constructor() {
    super('DungeonGate');
  }

  create(): void {
    const cx = GAME_WIDTH / 2;
    const g = this.add.graphics();
    paintInteriorBackdrop(g, GAME_WIDTH, GAME_HEIGHT, 0x2a3148, 0x1a1f2e);

    const gate = this.add.graphics();
    gate.fillStyle(0x3a4158, 1);
    gate.fillRect(cx - 70, 120, 140, 130);
    gate.fillStyle(0x12161f, 1);
    gate.fillRect(cx - 40, 155, 80, 95);
    gate.lineStyle(3, 0xc9a227, 0.8);
    gate.strokeRect(cx - 70, 120, 140, 130);
    this.tweens.add({
      targets: gate,
      alpha: 0.85,
      duration: 1400,
      yoyo: true,
      repeat: -1,
    });

    addBackToVillageButton(this);
    titleBlock(this, 'Dungeon Gate', 'Choose level and mutation, then enter.', 290);

    const store = createSaveStore();
    const existing = store.load();
    const dungeon = dungeonFromSave(existing);
    const canContinue =
      existing?.run &&
      existing.run.status !== 'settled' &&
      dungeon &&
      dungeon.outcome === 'ongoing';

    this.add
      .text(cx, 340, `${MINI_DUNGEON_NAME} · 5 encounters + boss`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#f2e6c8',
      })
      .setOrigin(0.5);

    const clears =
      existing?.permanent.dungeonCompletions.proto_cave?.clearCount ?? 0;
    this.add
      .text(cx, 362, clears > 0 ? `Clears: ${clears}` : 'Not yet cleared', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#9aa3c0',
      })
      .setOrigin(0.5);

    let y = 395;

    if (canContinue && dungeon && existing) {
      const label =
        dungeon.phase === 'reward'
          ? 'Continue (collect reward)'
          : `Continue (${dungeon.encounterIndex + 1}/${dungeon.encounterCount})`;
      addPrimaryButton(this, cx, y, label, () => {
        this.scene.start('Combat', {
          mode: 'dungeon',
          dungeonRun: dungeon,
          rngState: existing.rngState,
        });
      });
      y += 64;
      addSecondaryButton(this, cx, y, 'Abandon run…', () =>
        this.showAbandonConfirm(),
      );
    } else {
      y = this.renderLevelControl(cx, y);
      y = this.renderMutationPicker(cx, y + 8);
      y += 24;
      addPrimaryButton(this, cx, y, `Enter ${MINI_DUNGEON_NAME}`, () =>
        this.startNewRun(),
      );
    }

    this.add
      .text(
        cx,
        GAME_HEIGHT - 40,
        'Leaving mid-run via Village keeps your save. Abandon forfeits run XP & loot.',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#6e7694',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 40 },
        },
      )
      .setOrigin(0.5);
  }

  private renderLevelControl(cx: number, y: number): number {
    this.add
      .text(cx, y, 'Dungeon Level', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#b07a3a',
      })
      .setOrigin(0.5);
    y += 28;

    const minus = this.add
      .rectangle(cx - 70, y, 48, 40, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(cx - 70, y, '−', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#e8d5a3',
      })
      .setOrigin(0.5);

    this.levelLabel = this.add
      .text(cx, y, `Lv ${this.selectedDungeonLevel}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#f2e6c8',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const plus = this.add
      .rectangle(cx + 70, y, 48, 40, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(cx + 70, y, '+', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#e8d5a3',
      })
      .setOrigin(0.5);

    minus.on('pointerup', () => {
      this.selectedDungeonLevel = clampPlayableDungeonLevel(
        this.selectedDungeonLevel - 1,
      );
      this.refreshProgressionHint();
    });
    plus.on('pointerup', () => {
      this.selectedDungeonLevel = clampPlayableDungeonLevel(
        this.selectedDungeonLevel + 1,
      );
      this.refreshProgressionHint();
    });

    return y + 36;
  }

  private renderMutationPicker(cx: number, y: number): number {
    this.add
      .text(cx, y, 'Mutation', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#b07a3a',
      })
      .setOrigin(0.5);
    y += 28;

    const gap = 108;
    const startX = cx - gap;
    const refreshers: Array<() => void> = [];
    DUNGEON_MUTATIONS.forEach((id, i) => {
      const x = startX + i * gap;
      const bg = this.add
        .rectangle(x, y, 96, 40, 0x2a3350)
        .setStrokeStyle(1, 0xc9a227)
        .setInteractive({ useHandCursor: true });
      const label = this.add
        .text(x, y, MUTATION_LABELS[id], {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#e8d5a3',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const refresh = (): void => {
        const active = this.selectedMutation === id;
        bg.setFillStyle(active ? 0x3a4a28 : 0x2a3350);
        bg.setStrokeStyle(2, active ? 0xc9a227 : 0x6e7694);
        label.setColor(active ? '#f2e6c8' : '#9aa3c0');
      };
      refreshers.push(refresh);
      bg.on('pointerup', () => {
        this.selectedMutation = id;
        this.refreshProgressionHint();
        for (const fn of refreshers) fn();
      });
      refresh();
    });

    y += 36;
    this.progressionHint = this.add
      .text(cx, y, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#9aa3c0',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 48 },
      })
      .setOrigin(0.5);
    this.refreshProgressionHint();
    return y + 18;
  }

  private refreshProgressionHint(): void {
    this.levelLabel?.setText(`Lv ${this.selectedDungeonLevel}`);
    const copy = describeProgressionChallenge({
      dungeonLevel: this.selectedDungeonLevel,
      mutation: this.selectedMutation,
    });
    this.progressionHint?.setText(`${copy.challenge}\n${copy.rewards}`);
  }

  private startNewRun(): void {
    const store = createSaveStore();
    const previous = store.load();
    const permanent =
      previous?.permanent ?? createDefaultPermanentSave();
    const seed = Date.now() >>> 0;
    const rng = createSeededRng(seed);
    const classId = permanent.profile.activeClassId ?? 'vanguard';
    const classLevel = permanent.classes.vanguard.level ?? 1;
    const classRanks = permanent.classes.vanguard.talentRanks ?? {};
    const inventory = inventoryFromPermanent(permanent);
    const equipmentSnapshot = createEquipmentSnapshot(inventory);
    const equipment = buildEquipmentContextFromLoadout(inventory, classRanks);
    const combatStats = computeCharacterCombatStats(
      classRanks,
      equipment,
      permanent.general.talentRanks ?? {},
      { classId, classLevel },
    );
    const dungeonRun = startDungeonRun(rng, seed, {
      unlockedDiceSlots: permanent.unlockedDiceSlots,
      combatStats,
      unlockedActiveSkillIds: combatStats.unlockedActiveSkillIds,
      playerMaxHp: combatStats.maxHp,
      equipmentSnapshot,
      dungeonLevel: this.selectedDungeonLevel,
      mutation: this.selectedMutation,
    });
    store.save(buildSavedGame(dungeonRun, rng.state, { permanent }));
    this.scene.start('Combat', {
      mode: 'dungeon',
      dungeonRun,
      rngState: rng.state,
    });
  }

  private showAbandonConfirm(): void {
    if (this.abandonConfirm) {
      return;
    }
    const cx = GAME_WIDTH / 2;
    const veil = this.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.75)
      .setOrigin(0)
      .setInteractive();
    const panel = this.add
      .rectangle(cx, GAME_HEIGHT * 0.45, GAME_WIDTH - 48, 220, 0x161b2e)
      .setStrokeStyle(2, 0xc45c5c);
    const title = this.add
      .text(cx, GAME_HEIGHT * 0.45 - 70, 'Abandon this run?', {
        fontFamily: 'Georgia, serif',
        fontSize: '20px',
        color: '#c45c5c',
      })
      .setOrigin(0.5);
    const body = this.add
      .text(
        cx,
        GAME_HEIGHT * 0.45 - 20,
        "Abandoning loses this run's XP and loot.",
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#e8d5a3',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 80 },
        },
      )
      .setOrigin(0.5);

    const confirmBg = this.add
      .rectangle(cx - 70, GAME_HEIGHT * 0.45 + 60, 120, 44, 0xc45c5c)
      .setInteractive({ useHandCursor: true });
    const confirmLabel = this.add
      .text(cx - 70, GAME_HEIGHT * 0.45 + 60, 'Abandon', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#fff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const cancelBg = this.add
      .rectangle(cx + 70, GAME_HEIGHT * 0.45 + 60, 120, 44, 0x2a3350)
      .setInteractive({ useHandCursor: true });
    const cancelLabel = this.add
      .text(cx + 70, GAME_HEIGHT * 0.45 + 60, 'Keep run', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#e8d5a3',
      })
      .setOrigin(0.5);

    const container = this.add.container(0, 0, [
      veil,
      panel,
      title,
      body,
      confirmBg,
      confirmLabel,
      cancelBg,
      cancelLabel,
    ]);
    container.setDepth(1000);
    this.abandonConfirm = container;

    const close = (): void => {
      container.destroy(true);
      this.abandonConfirm = undefined;
    };
    cancelBg.on('pointerup', close);
    confirmBg.on('pointerup', () => {
      this.performAbandon();
      close();
    });
  }

  private performAbandon(): void {
    const store = createSaveStore();
    const previous = store.load();
    if (!previous?.run || !previous.permanent) {
      this.scene.start('Village');
      return;
    }
    const marked = markAwaitingSettlement(previous.run, 'abandon');
    const settled = settleRun(previous.permanent, marked, 'abandon');
    store.save({
      version: SAVE_SCHEMA_VERSION,
      savedAt: Date.now(),
      rngState: previous.rngState,
      permanent: settled.permanent,
      run: null,
    });
    this.scene.start('Village');
  }
}
